/**
 * The home feed's tag filter lives in the URL — `/?tag=fed`, or together
 * with a category `/?crypto&tag=bitcoin` — so a filtered view can be shared,
 * bookmarked and reached with the back button, the way Polymarket's
 * `/?tag=<slug>` works. Tags come from the event service as display names
 * ("sanctions & trade"); the URL carries a slug and the name is looked up
 * again from the loaded tag list.
 */

export const TAG_PARAM = 'tag';

/** `"Sanctions & trade"` → `"sanctions-and-trade"`. */
export function tagToSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Tag name for a URL slug. Prefers an exact match in the known tag list; a
 * deep link to a tag outside the popular list falls back to reversing the
 * slug, which is exact for single words and close enough for the API's
 * case-insensitive match otherwise.
 */
export function resolveTagFromSlug(
  slug: string | null | undefined,
  tags?: ReadonlyArray<{ name: string }>,
): string {
  if (!slug) return '';
  const match = tags?.find((tag) => tagToSlug(tag.name) === slug);
  if (match) return match.name;
  return slug.replace(/-and-/g, ' & ').replace(/-/g, ' ');
}

/**
 * Root category slug from the feed's search params. Categories are bare keys
 * (`?crypto`), so the category is the first key with an empty value that is
 * not the tag parameter.
 */
export function categoryFromSearchParams(
  params: Iterable<[string, string]> | Record<string, string | string[] | undefined>,
): string {
  const entries: Array<[string, string]> =
    Symbol.iterator in Object(params)
      ? [...(params as Iterable<[string, string]>)]
      : Object.entries(params as Record<string, string | string[] | undefined>).map(
          ([key, value]) => [key, Array.isArray(value) ? (value[0] ?? '') : (value ?? '')],
        );
  return entries.find(([key, value]) => key !== TAG_PARAM && value === '')?.[0] ?? '';
}

/** Query string for the feed: bare category key first, then `tag=<slug>`. */
export function buildFeedQuery(category: string, tag: string): string {
  const parts: string[] = [];
  if (category && category !== 'all') parts.push(encodeURIComponent(category));
  if (tag) parts.push(`${TAG_PARAM}=${encodeURIComponent(tagToSlug(tag))}`);
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * Reflect a tag selection in the address bar without a navigation. Next's
 * `useSearchParams` follows `history.pushState`, so the filter chips re-read
 * the URL and the store follows; pushing (not replacing) keeps the back
 * button working across tag changes.
 */
export function pushTagToUrl(category: string, tag: string): void {
  if (typeof window === 'undefined') return;
  const next = `${window.location.pathname}${buildFeedQuery(category, tag)}`;
  const current = `${window.location.pathname}${window.location.search}`;
  if (next === current) return;
  window.history.pushState(null, '', next);
}
